import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Package, Camera, X, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API } from "@/utils/constants";

export const PartsRequestDialog = ({ open, onClose, vehicles, currentUser, onSuccess }) => {
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [odometer, setOdometer] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSelectedVehicle(null);
      setOdometer("");
      setQuantity("1");
      setNotes("");
      setPhotos([]);
    }
  }, [open]);

  const handleVehicleSelect = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    setSelectedVehicle(vehicle);
    // Pre-fill odometer with current mileage if available
    if (vehicle?.mileage) {
      setOdometer(vehicle.mileage.toLocaleString());
    }
  };

  const handleOdometerChange = (e) => {
    const value = e.target.value.replace(/,/g, '');
    if (value === '' || /^\d+$/.test(value)) {
      const numValue = parseInt(value) || 0;
      setOdometer(numValue > 0 ? numValue.toLocaleString() : '');
    }
  };

  const handleQuantityChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      setQuantity(value);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Each image must be less than 5MB");
        continue;
      }

      // Compress and convert to base64
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxWidth = 1200;
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          setPhotos(prev => [...prev, canvas.toDataURL('image/jpeg', 0.7)]);
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!selectedVehicle) {
      toast.error("Please select a vehicle");
      return;
    }

    if (!notes.trim()) {
      toast.error("Please enter a part description");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        vehicle_id: selectedVehicle.id,
        unit_number: selectedVehicle.unit_number,
        odometer: odometer ? parseInt(odometer.replace(/,/g, '')) : null,
        quantity: quantity ? parseInt(quantity) : 1,
        notes: notes.trim(),
        photos: photos,
        requested_by: `${currentUser.first_name} ${currentUser.last_name}`,
        requested_by_id: currentUser.id
      };

      await axios.post(`${API}/parts-requests`, payload);
      toast.success("Parts request submitted!");
      onClose();
      onSuccess?.();
    } catch (error) {
      console.error("Failed to submit parts request", error);
      toast.error("Failed to submit parts request");
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = selectedVehicle && notes.trim();

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="w-screen h-[100dvh] max-w-none max-h-[100dvh] m-0 rounded-none flex flex-col fixed inset-0 p-0 overflow-hidden sm:max-w-lg sm:h-auto sm:max-h-[90vh] sm:rounded-xl sm:m-auto">
        <DialogHeader className="flex-shrink-0 p-4 border-b bg-blue-50">
          <DialogTitle className="flex items-center gap-2 text-blue-700">
            <Package className="w-5 h-5" />
            Parts Request
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-4 space-y-5" style={{ WebkitOverflowScrolling: 'touch' }}>
          {/* Select Vehicle */}
          <div>
            <Label className="text-base font-semibold">Select Vehicle *</Label>
            <Select value={selectedVehicle?.id || ""} onValueChange={handleVehicleSelect}>
              <SelectTrigger className="h-12 mt-2" data-testid="select-parts-vehicle">
                <SelectValue placeholder="Choose a vehicle..." />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {vehicles.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    Unit {v.unit_number} - {v.year} {v.make} {v.model}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Odometer Reading (Optional) */}
          <div>
            <Label className="text-base font-semibold">Odometer Reading (Optional)</Label>
            <Input
              value={odometer}
              onChange={handleOdometerChange}
              placeholder="e.g., 150,000"
              className="h-12 mt-2 text-lg"
              inputMode="numeric"
              data-testid="input-parts-odometer"
            />
            {selectedVehicle?.mileage && (
              <p className="text-xs text-slate-500 mt-1">
                Current recorded: {selectedVehicle.mileage.toLocaleString()} miles
              </p>
            )}
          </div>

          {/* Parts Description */}
          <div>
            <Label className="text-base font-semibold">Part Description *</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Describe the parts needed, include part numbers if known..."
              className="min-h-[100px] mt-2"
              data-testid="input-parts-notes"
            />
          </div>

          {/* Quantity */}
          <div>
            <Label className="text-base font-semibold">Quantity</Label>
            <Input
              value={quantity}
              onChange={handleQuantityChange}
              placeholder="1"
              className="h-12 mt-2 text-lg w-32"
              inputMode="numeric"
              data-testid="input-parts-quantity"
            />
          </div>

          {/* Photos */}
          <div>
            <Label className="text-base font-semibold">Photos (Optional)</Label>
            <p className="text-sm text-slate-500 mt-1 mb-3">Add photos of the part or area needing replacement</p>

            {photos.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-3">
                {photos.map((photo, index) => (
                  <div key={index} className="relative rounded-lg overflow-hidden border-2 border-slate-200">
                    <img src={photo} alt={`Part ${index + 1}`} className="w-full h-24 object-cover" />
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow-lg"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
              <Camera className="w-8 h-8 text-slate-400 mb-2" />
              <span className="text-sm text-slate-600 font-medium">Tap to add photos</span>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                onChange={handlePhotoUpload}
                className="hidden"
                data-testid="input-parts-photos"
              />
            </label>
          </div>

          {/* User stamp preview */}
          <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
            <p><span className="font-medium">Requested by:</span> {currentUser?.first_name} {currentUser?.last_name}</p>
            <p><span className="font-medium">Date:</span> {new Date().toLocaleString()}</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 p-4 border-t bg-white flex gap-3" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
          <Button variant="outline" onClick={onClose} className="flex-1 h-12">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className="flex-1 h-12 bg-blue-600 hover:bg-blue-700"
            data-testid="btn-submit-parts-request"
          >
            {submitting ? "Submitting..." : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Submit Request
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
